import React from 'react';
import PropTypes from 'prop-types';
import { f7, Icon, Page } from 'framework7-react';

import II18 from './translate/II18';
import { gae, gaeApp, gaeAppNavigate, gaPageView } from './helpers/Analytics';
import {
  extractDemoRequestId,
  isAppOnline,
  REQUEST_TYPE,
  resetRequestIdCookie
} from '../lib/utils';
import { getCachedTemplate } from '../lib/templateCache';
import {
  consumePendingStandaloneDraftRestore,
  getStandaloneDraft,
  applyStandaloneDraftPendingSourcePdfs,
  updateStandaloneDraftPwaSession
} from '../lib/standaloneDraftCache';
import { f7navigator, initF7Navigator, PATHS } from './helpers/navigator';
import {
  beginJourney,
  endJourney,
  JOURNEY,
  setJourneyAttributes,
  noteActiveRequestId
} from '../telemetry/tracing';

class Load extends React.Component {
  componentDidMount() {
    gaPageView('PreLoad');

    initF7Navigator(this.$f7router, this.props.scanStarted);
    window.iosVersion = this.$f7.device.ios && this.$f7.device.osVersion;

    if (!this.props.startedFromQR && this.props.scanStarted) {
      this.startApp();
    } else {
      this.props.resetAndStartAppNew(this.props.requestInfo);
    }
  }

  listRequestedDocuments(id, templateMode) {
    if (templateMode) {
      if (!isAppOnline() && window.standalone) {
        gaeApp('TEMPLATE_CACHE', 'LOAD_DECIDE_CACHE', id);
        this.props.requestListTemplateDocuments();
        getCachedTemplate(id).then(cached => {
          if (cached) {
            gaeApp('TEMPLATE_CACHE', 'LOAD_APPLY_CACHE', id);
            this.props.receiveListRequestedDocumentsTemplate(cached, id);
          } else {
            gaeApp('TEMPLATE_CACHE', 'LOAD_CACHE_MISS_USE_NETWORK', id);
            this.props.listRequestedDocumentsTemplate(id);
          }
        });
      } else {
        gaeApp('TEMPLATE_CACHE', 'LOAD_DECIDE_NETWORK', id);
        this.props.listRequestedDocumentsTemplate(id);
      }
    } else {
      this.props.listRequestedDocuments(id);
    }
  }

  prepareRequest() {
    const {
      requestInfo,
      recipientRequestId,
      createDemoRequest,
      getCrossDeviceRequest
    } = this.props;

    if (!recipientRequestId) {
      gae('LOAD', 'PREPARE_REQUEST', JSON.stringify({ requestInfo }, null, 2));

      const loadType =
        (requestInfo && requestInfo.requestType) || REQUEST_TYPE.COMMON_REQUEST;
      if (requestInfo && requestInfo.requestId) {
        noteActiveRequestId(requestInfo.requestId, {
          'request.change_reason': 'load.prepare',
          'request.load_type': String(loadType)
        });
      }
      beginJourney(JOURNEY.LOAD, 'request.load', {
        'request.load_type': loadType,
        'request.template_mode': Boolean(
          loadType === REQUEST_TYPE.TEMPLATE ||
            loadType === REQUEST_TYPE.DEMO_TEMPLATE
        ),
        'app.started_from_qr': Boolean(this.props.startedFromQR),
        'app.embed': Boolean(this.props.embedMode),
        'app.standalone': Boolean(window.standalone)
      });

      f7.dialog.preloader(II18.get('title_loading'));
      switch (requestInfo?.requestType) {
        case REQUEST_TYPE.FILE_DOWNLOAD:
          endJourney(JOURNEY.LOAD, 'ok', {
            'request.load_result': 'navigate_file_download'
          });
          this.navigateAppTo(PATHS.FILE_DOWNLOAD);
          break;
        case REQUEST_TYPE.SIGN_NOW:
          endJourney(JOURNEY.LOAD, 'ok', {
            'request.load_result': 'navigate_sign_now'
          });
          this.navigateAppTo(PATHS.SIGN_NOW);
          break;
        case REQUEST_TYPE.CROSS_DEVICE_DOCUMENT:
          getCrossDeviceRequest(requestInfo.requestId);
          break;
        case REQUEST_TYPE.DEMO_TEMPLATE:
        case REQUEST_TYPE.TEMPLATE:
          this.listRequestedDocuments(requestInfo.requestId, true);
          break;
        case REQUEST_TYPE.COMMON_REQUEST:
          if (requestInfo.isRequestIdADemoRequest) {
            createDemoRequest(extractDemoRequestId(requestInfo.requestId));
          } else {
            this.listRequestedDocuments(requestInfo.requestId);
          }
          break;
        default:
          endJourney(JOURNEY.LOAD, 'ok', {
            'request.load_result': 'start_without_request'
          });
          this.startApp();
      }
    } else {
      beginJourney(JOURNEY.LOAD, 'request.load', {
        'request.load_type': 'reload',
        'request.template_mode': Boolean(this.props.templateMode)
      });
      this.listRequestedDocuments(recipientRequestId, this.props.templateMode);
    }
  }

  componentDidUpdate() {
    const {
      requestedDocuments,
      scanStarted,
      recipientRequestId,
      errorMessage,
      infoMessage,
      authOnStartRequired,
      isFetching
    } = this.props;

    //TODO: in case request has a pin or smsTan analyticsDisabled will not appear
    if (this.props.analyticsDisabled === false && !this.props.embedMode) {
      const banner = document.getElementsByClassName(
        'react-cookie-law-dialog'
      )[0];
      if (banner) banner.style.display = 'block';
    }

    const requestDataLoaded = recipientRequestId && requestedDocuments;
    const receivedAdditionalInfoOrError =
      errorMessage || infoMessage || authOnStartRequired;

    if (!isFetching && !scanStarted && !this.appStartScheduled) {
      if (requestDataLoaded || receivedAdditionalInfoOrError) {
        if (receivedAdditionalInfoOrError) {
          gae(
            'LOAD',
            'CANT_LOAD_REQUEST',
            JSON.stringify(
              { errorMessage, infoMessage, authOnStartRequired },
              null,
              2
            )
          );
          if (authOnStartRequired && !errorMessage) {
            setJourneyAttributes(JOURNEY.LOAD, {
              'request.auth_required': true
            });
            endJourney(JOURNEY.LOAD, 'ok', {
              'request.load_result': 'auth_gate'
            });
          } else {
            endJourney(
              JOURNEY.LOAD,
              'error',
              errorMessage || infoMessage || 'load_failed'
            );
          }
        } else {
          setJourneyAttributes(JOURNEY.LOAD, {
            'request.doc_count': Array.isArray(requestedDocuments)
              ? requestedDocuments.length
              : 0,
            'request.template_mode': Boolean(this.props.templateMode)
          });
          endJourney(JOURNEY.LOAD, 'ok', {
            'request.load_result': 'ready'
          });
        }
        this.appStartScheduled = true;
        this.restoreUserDataForStandaloneApp();
        this.restoreStandaloneDraftIfNeeded().then(() => {
          this.startApp();
        });
      } else {
        this.prepareRequest();
      }
    }
  }

  restoreStandaloneDraftIfNeeded = () => {
    if (
      !window.draftEnabled ||
      !this.props.templateMode ||
      !consumePendingStandaloneDraftRestore()
    ) {
      return Promise.resolve();
    }

    return getStandaloneDraft().then(draft => {
      if (!draft?.state || draft.templateId !== this.props.recipientRequestId) {
        return;
      }
      gaeApp('STANDALONE_DRAFT', 'RESTORE', draft.templateId);
      applyStandaloneDraftPendingSourcePdfs(draft.state);
      updateStandaloneDraftPwaSession(draft.templateId);
      this.props.receiveStandaloneDraft(draft.state);
    });
  };

  restoreUserDataForStandaloneApp = () => {
    /*if (
      this.props.templateMode &&
      window.standalone &&
      !window.standaloneUsersDataRestored
    ) {
      window.standaloneUsersDataRestored = true;
      const usersData = this.props.usersData;
      const savedUserData = getTemplatesUserData(usersData[0].userFields);

      if (savedUserData) {
        gaeApp('RESTORE_STANDALONE_SAVED_USERS_DATA');
        this.props.setUsersData(
          arrUpdate(usersData, 0, {
            userName: usersData[0].userName || savedUserData.userName,
            userEmail: usersData[0].userEmail || savedUserData.userEmail,
            userFields: savedUserData.userFields
          })
        );
      }
    }*/
  };

  startApp = () => {
    gae('LOAD', 'START_REQUEST');
    window.recipientRequestId = this.props.recipientRequestId;
    this.navigateAppTo(PATHS.HOME);
  };

  navigateAppTo = path => {
    const navProps = {
      animate: false,
      clearPreviousHistory: true,
      force: true,
      reloadAll: true
    };
    setTimeout(
      () => {
        f7.dialog.close();
        f7.dialog.destroy();

        gaeAppNavigate(path);
        f7navigator.toPath(
          path,
          { startedFromQR: this.props.startedFromQR },
          navProps
        );
      },
      this.props.startedFromQR ? (this.$f7.device.ios ? 700 : 300) : 10
    );
  };

  render = () => {
    if (this.props.closed || this.props.submitted) {
      resetRequestIdCookie();
    }
    /**
     * Show icons to force fonts load.
     */
    return (
      <Page style={this.$f7?.device?.ios ? { background: '#4e4e4e' } : {}}>
        <Icon material="highlight_off" color="white" size={1} />
        <Icon icon={'fas fa-check'} color="white" size={1} />
        <Icon f7="lock" color="white" size={1} />
      </Page>
    );
  };
}

Load.propTypes = {
  embedMode: PropTypes.bool,
  isFetching: PropTypes.bool,
  caseName: PropTypes.string,
  companyName: PropTypes.string,
  recipientRequestId: PropTypes.string,
  listRequestedDocuments: PropTypes.func,
  listRequestedDocumentsTemplate: PropTypes.func,
  requestListTemplateDocuments: PropTypes.func,
  receiveListRequestedDocumentsTemplate: PropTypes.func,
  createDemoRequest: PropTypes.func,
  requestedDocuments: PropTypes.object,
  brandingDetails: PropTypes.object,
  f7router: PropTypes.object,
  userFields: PropTypes.array,
  closed: PropTypes.bool,
  submitted: PropTypes.bool,
  scanStarted: PropTypes.any,
  templateMode: PropTypes.bool,
  startedFromQR: PropTypes.bool,
  scanEnded: PropTypes.bool,
  infoMessage: PropTypes.string,
  errorMessage: PropTypes.string,
  resetAndStartAppNew: PropTypes.func,
  setUsersData: PropTypes.func,
  receiveStandaloneDraft: PropTypes.func,
  usersData: PropTypes.array,
  authOnStartRequired: PropTypes.bool,
  analyticsDisabled: PropTypes.bool,
  getCrossDeviceRequest: PropTypes.func,
  requestInfo: PropTypes.object,
  crossDeviceRequest: PropTypes.object
};

export default Load;
